import { roundToTwoDecimals, toUpperCase, isValidString, getOrgDisplayLabel } from "../../common/CommonUtilsMethods";
import { BillingTimeInterval, OrgJobberMatchingType } from "../../common/CommonInterfaces";
import { AgencyObjClient, CreateOrgForAgencyFormData, CreateOrgToAgencyObjReqDTO, OrgHourlyRateMarkupType, CreateOrgLocationClient } from "../agency-data/AgencyInterfaces";
import { WorkJurisdictionIdentifier } from "../../orgViews/orgConstants";
import {
  deriveBillingDayNameFromAnchorDateEastern,
  epochSecFromDateInputEasternStartOfDay,
} from "../../direct-invoicing/utils-methods/DirectInvoicingEasternDateUtils";

const isWeeklyOrBiWeeklyBillingInterval = (billingInterval: BillingTimeInterval | string): boolean => {
  return billingInterval === BillingTimeInterval.WEEKLY || billingInterval === BillingTimeInterval.BI_WEEKLY;
};

const validateBillingCycleAnchorFormFields = (
  formData: CreateOrgForAgencyFormData,
  newErrors: Record<string, boolean>,
): void => {
  if (!isWeeklyOrBiWeeklyBillingInterval(formData.billingInterval)) {
    return;
  }
  if (!isValidString(formData.billingCycleAnchorDate)) {
    newErrors.billingCycleAnchorDate = true;
  }
};

const resolveBillingCycleAnchorEpochSecForPayload = (
  formData: CreateOrgForAgencyFormData,
): number | undefined => {
  if (!isWeeklyOrBiWeeklyBillingInterval(formData.billingInterval)) {
    return undefined;
  }
  const epochSec: number | null = epochSecFromDateInputEasternStartOfDay(formData.billingCycleAnchorDate);
  if (epochSec === null) {
    return undefined;
  }
  return epochSec;
};

const determineDefaultOrgBillingPayRate = ({
  defaultJobberPayRate,
  orgHourlyRateMarkupType,
  defaultOrgBillingPayRate,
}: {
  defaultJobberPayRate: number;
  orgHourlyRateMarkupType: OrgHourlyRateMarkupType;
  defaultOrgBillingPayRate: number;
}): number => {
  if (orgHourlyRateMarkupType == OrgHourlyRateMarkupType.HOURLY_RATE_MARKUP_TYPE) {
    return defaultJobberPayRate + defaultOrgBillingPayRate;
  } else if (orgHourlyRateMarkupType == OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE) {
    return defaultJobberPayRate * (1 + (defaultOrgBillingPayRate ?? 0) / 100);
  } else if (
    orgHourlyRateMarkupType == OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE &&
    defaultOrgBillingPayRate >= defaultJobberPayRate
  ) {
    return defaultOrgBillingPayRate;
  } else {
    return defaultJobberPayRate;
  }
};

const calculateOrgBillingRate = (workerPay: number, markupType: OrgHourlyRateMarkupType, markupValue: number = 0): number => {
  console.log({ name: "calculateOrgBillingRate", workerPay, markupType, markupValue });
  switch (markupType) {
    case OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE:
      return roundToTwoDecimals(workerPay * (1 + markupValue / 100));
    case OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE:
      return markupValue;
    case OrgHourlyRateMarkupType.HOURLY_RATE_MARKUP_TYPE:
      return Number(workerPay) + Number(markupValue);
    default:
      return workerPay;
  }
};

const parseEmailsFromString = (inputString: string): string[] => {
  if (!inputString) {
      return [];
  }

  // Regular expression for matching emails
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

  // First, try to extract all emails directly using the regex
  const matches = inputString.match(emailRegex);

  if (!matches) {
      return [];
  }

  // Deduplicate and validate emails
  const validEmails: string[] = [];
  const seen = new Set<string>();

  for (const match of matches) {
      const trimmedEmail = match.trim().toLowerCase();
      
      // Additional validation checks
      if (
          trimmedEmail &&
          !seen.has(trimmedEmail) &&
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmedEmail)
      ) {
          console.log("trimmedEmail", trimmedEmail);
          validEmails.push(match.trim()); // Keep original casing
          seen.add(trimmedEmail);
      }
  }

  return validEmails;
};

// // Quick tests
// console.log("Test 1 (empty string):", parseEmailsFromString("")); // Expected: []
// console.log("Test 2 (null/undefined):", parseEmailsFromString(null!)); // Expected: []
// console.log("Test 3 (valid emails):", parseEmailsFromString("test@example.com, another@domain.com")); // Expected: ["test@example.com", "another@domain.com"]
// console.log("Test 4 (invalid emails):", parseEmailsFromString("not-an-email, another@invalid")); // Expected: []
// console.log("Test 5 (mixed valid/invalid):", parseEmailsFromString("valid@example.com, invalid, another@valid.com")); // Expected: ["valid@example.com", "another@valid.com"]
// console.log("Test 6 (split by .com):", parseEmailsFromString("email1@example.comemail2@domain.com")); // Expected: ["email1@example.com", "email2@domain.com"]

// console.log(
//     "Test 7 (valid emails with + and mixed separators):",
//     parseEmailsFromString(
//         "mayyarsecond+AgencyOneQAJobberTommy@gmail.commayyarsecond+AgencyOneQAJobberJanet@gmail.commayyarsecond+AgencyOneQAJobberCliue@gmail.com"
//     )
// ); // Expected: ["mayyarsecond+AgencyOneQAJobberTommy@gmail.com", "mayyarsecond+AgencyOneQAJobberJanet@gmail.com", "mayyarsecond+AgencyOneQAJobberCliue@gmail.com"]

// console.log(
//     "Test 8 (valid emails with spaces):",
//     parseEmailsFromString(
//         "mayyarsecond+AgencyOneQAJobberTommy@gmail.com mayyarsecond+AgencyOneQAJobberJanet@gmail.com mayyarsecond+AgencyOneQAJobberCliue@gmail.com"
//     )
// ); // Expected: ["mayyarsecond+AgencyOneQAJobberTommy@gmail.com", "mayyarsecond+AgencyOneQAJobberJanet@gmail.com", "mayyarsecond+AgencyOneQAJobberCliue@gmail.com"]

// console.log(
//     "Test 9 (valid emails with commas):",
//     parseEmailsFromString(
//         "mayyarsecond+AgencyOneQAJobberTommy@gmail.com, mayyarsecond+AgencyOneQAJobberJanet@gmail.com, mayyarsecond+AgencyOneQAJobberCliue@gmail.com"
//     )
// ); // Expected: ["mayyarsecond+AgencyOneQAJobberTommy@gmail.com", "mayyarsecond+AgencyOneQAJobberJanet@gmail.com", "mayyarsecond+AgencyOneQAJobberCliue@gmail.com"]

// console.log(
//     "Test 10 (valid emails concatenated without separators):",
//     parseEmailsFromString(
//         "mayyarsecond+AgencyOneQAJobberTommy@gmail.commayyarsecond+AgencyOneQAJobberJanet@gmail.commayyarsecond+AgencyOneQAJobberCliue@gmail.com"
//     )
// ); // Expected: ["mayyarsecond+AgencyOneQAJobberTommy@gmail.com", "mayyarsecond+AgencyOneQAJobberJanet@gmail.com", "mayyarsecond+AgencyOneQAJobberCliue@gmail.com"]

// console.log(
//     "Test 11 (valid emails with mixed separators):",
//     parseEmailsFromString(
//         "mayyarsecond+AgencyOneQAJobberTommy@gmail.com, ,,mayyarsecond+AgencyOneQAJobberJanet@gmail.com|mayyarsecond+AgencyOneQAJobberCliue@gmail.com"
//     )
// ); // Expected: ["mayyarsecond+AgencyOneQAJobberTommy@gmail.com", "mayyarsecond+AgencyOneQAJobberJanet@gmail.com", "mayyarsecond+AgencyOneQAJobberCliue@gmail.com"]

const calculateOrgBillingRateFormData = (formData: CreateOrgForAgencyFormData):number => {
  if(!formData || !formData?.workerType || !formData?.workerType[0]?.workerTypeName){
    return calculateOrgBillingRate(0,OrgHourlyRateMarkupType.HOURLY_RATE_MARKUP_TYPE,0);
  }

  return calculateOrgBillingRate(formData?.workerType[0]?.defaultJobberPayRate, formData?.workerType[0]?.orgHourlyRateMarkupType, formData?.workerType[0]?.defaultOrgBillingPayRate);
}


const GeneratePayloadForCreateOrgToAgencyObjReqDTO = (agencyData, formData: CreateOrgForAgencyFormData): CreateOrgToAgencyObjReqDTO => {
  const configuredWorkerTypes = (formData?.workerType ?? []).filter(
    (workerType) => isValidString(workerType?.workerTypeUniqueIdentifier)
  );

  const agencyOrgJobberWorkerTypeDetailsDTOMap =
    configuredWorkerTypes.length > 0
      ? configuredWorkerTypes.map((workerType) => ({
          createJobberWorkerTypeDTO: {
            workerTypeUniqueIdentifier: workerType.workerTypeUniqueIdentifier,
            defaultJobberPayRate: workerType.defaultJobberPayRate,
            orgHourlyRateMarkupType: workerType.orgHourlyRateMarkupType,
            orgHourlyRateMarkupTypeValue: calculateOrgBillingRateFormData(formData),
          },
        }))
      : undefined;

  // Map org locations from the form data
  const orgLocationList: CreateOrgLocationClient[] = formData?.orgLocationClient?.map(location => ({
    orgName: formData.orgName,
    locationName: location.locationName || formData.orgName,
    locationAddress: location.locationAddress,
    servicedCity: location.servicedCity,
    workJurisdictionIdentifier:
      location.workJurisdictionIdentifier ?? WorkJurisdictionIdentifier.ONTARIO,
    // primaryShiftManagerContact: {
    //   userEmail: { value: formData.managerEmail },
    //   userPhoneNumber: { value: formData.managerPhoneNum },
    //   lastName: formData.managerName.split(" ").pop() || "",
    //   firstName: formData.managerName.split(" ")[0] || formData.managerName
    // },
    // primaryHRManagerContact: location.primaryHRManagerContact || {
    //   userEmail: { value: formData.managerEmail },
    //   userPhoneNumber: { value: formData.managerPhoneNum },
    //   lastName: formData.managerName.split(" ").pop() || "",
    //   firstName: formData.managerName.split(" ")[0] || formData.managerName
    // },
    orgUserContactsForLocation: location.orgUserContactsForLocation || [],
    orgJobberMatchingType: location.orgJobberMatchingType || null,
    createOrgClientHourlyDeductionConfig: (location as CreateOrgLocationClient)?.createOrgClientHourlyDeductionConfig
  }));

  const anchor: number | undefined = resolveBillingCycleAnchorEpochSecForPayload(formData);
  const workWeekDayFromAnchor: string | null = isWeeklyOrBiWeeklyBillingInterval(formData.billingInterval)
    ? deriveBillingDayNameFromAnchorDateEastern(formData.billingCycleAnchorDate)
    : null;
  const workJurisdictionIdentifier: WorkJurisdictionIdentifier =
    formData.orgLocationClient?.[0]?.workJurisdictionIdentifier ?? WorkJurisdictionIdentifier.ONTARIO;

  return {
    orgName: formData.orgName,
    addressDto: formData.locationAddress,
    corpCustomerShiftDetails: {
      isLunchBreakCovered: formData.isLunchBreakCovered,
      workWeekStartDay: workWeekDayFromAnchor ?? toUpperCase(formData.billingDay),
      workWeekEndDay: workWeekDayFromAnchor ?? toUpperCase(formData.billingDay)
    },
    communicationPreferences: {
      defaultEmailAddress: formData.managerEmail,
      defaultPhoneNumber: formData.managerPhoneNum,
    },
    orgDetailsCreateReqDTO: {
      orgLocationList: orgLocationList,
      billingTimeInterval: formData.billingInterval,
      workJurisdictionIdentifier,
      ...(anchor !== undefined ? { anchor } : {}),
      ...(agencyOrgJobberWorkerTypeDetailsDTOMap !== undefined
        ? { agencyOrgJobberWorkerTypeDetailsDTOMap }
        : {}),
    }
  };
};

/** Type for org details that have an optional displayName (e.g. from agencyShiftDetailsMapForEachOrg). */
type OrgDetailsWithDisplayName = { displayName?: string | null };

const getOrgDetailsMapRaw = (
  agencyData: AgencyObjClient | null
): Map<string, OrgDetailsWithDisplayName> | Record<string, OrgDetailsWithDisplayName> | undefined => {
  const raw = agencyData?.agencyShiftDetailsMapForEachOrg as unknown as
    | Map<string, OrgDetailsWithDisplayName>
    | Record<string, OrgDetailsWithDisplayName>
    | undefined;
  return raw ?? undefined;
};

/**
 * Returns the display label for a single org: display name if set, otherwise org key.
 */
const getDisplayLabelForOrg = (agencyData: AgencyObjClient | null, orgNameKey: string): string => {
  if (!orgNameKey) return orgNameKey;
  const raw = getOrgDetailsMapRaw(agencyData);
  if (!raw) return getOrgDisplayLabel(orgNameKey, null);
  let orgDetails: OrgDetailsWithDisplayName | undefined;
  if (raw instanceof Map) {
    orgDetails = raw.get(orgNameKey);
  } else if (typeof raw === 'object' && raw !== null && orgNameKey in raw) {
    orgDetails = raw[orgNameKey];
  }
  return getOrgDisplayLabel(orgNameKey, orgDetails);
};

/**
 * Builds a map of org key → display label (display name if set, otherwise org key).
 */
const buildOrgDisplayNameMap = (agencyData: AgencyObjClient | null): Record<string, string> => {
  const result: Record<string, string> = {};
  const raw = getOrgDetailsMapRaw(agencyData);
  if (!raw) return result;
  if (raw instanceof Map) {
    raw.forEach((orgDetails, orgName) => {
      result[orgName] = getOrgDisplayLabel(orgName, orgDetails);
    });
  } else if (typeof raw === 'object' && raw !== null) {
    for (const [orgName, orgDetails] of Object.entries(raw)) {
      result[orgName] = getOrgDisplayLabel(orgName, orgDetails);
    }
  }
  return result;
};

export {
  calculateOrgBillingRateFormData,
  GeneratePayloadForCreateOrgToAgencyObjReqDTO,
  isWeeklyOrBiWeeklyBillingInterval,
  parseEmailsFromString,
  calculateOrgBillingRate,
  determineDefaultOrgBillingPayRate,
  getDisplayLabelForOrg,
  buildOrgDisplayNameMap,
  validateBillingCycleAnchorFormFields,
};
